import React, { useState, useEffect } from 'react';
import { 
    Users, Briefcase, AlertCircle, TrendingUp, Clock, Settings, 
    Plus, FileText, UserCheck, Star, Sparkles, ClipboardList, 
    Target, ShieldCheck, Key, ChevronRight, Edit2, XCircle, 
    Trash2, CheckCircle, DollarSign, Download, History, Lock,
    Calendar, Save, BarChart3, Sun, Moon, Sunrise,
    Send, ShieldAlert, AlertTriangle
} from 'lucide-react';

import { MOCK_CLIENTS_FULL } from '../data/mockData';
import { HybridUnifiedWidget } from '../components/widgets/DashboardWidgets';
import { supabase } from '../supabaseClient';

// --- INITIAL MOCK STATE
const INITIAL_STAFF = [
    { 
        id: 'ST-001', name: 'Sarah Connor', role: 'Sales_L1', 
        metrics: { sales: 12, calls: 450, csat: 4.8 },
        shifts: { Mon: '09:00 - 17:00', Tue: '09:00 - 17:00', Wed: 'Off', Thu: '09:00 - 17:00', Fri: '09:00 - 16:00' },
        leave: { allowance: 25, taken: 12, sick: 1, pending: 0, nationalHolidays: 2 },
        // ADDED: Past Reviews
        reviews: [
            { id: 101, date: '2025-10-15', status: 'Completed', type: 'Quarterly', rating: 9, manager: 'Sales_Manager' },
            { id: 102, date: '2025-07-15', status: 'Completed', type: 'Quarterly', rating: 8, manager: 'Sales_Manager' },
            { id: 103, date: '2026-02-15', status: 'Scheduled', type: 'Performance' } // Future one
        ]
    },
    { 
        id: 'ST-002', name: 'Kyle Reese', role: 'Support_L1', 
        metrics: { tickets: 150, kyc: 40, csat: 4.5 },
        shifts: { Mon: '08:00 - 16:00', Tue: '08:00 - 16:00', Wed: '08:00 - 16:00', Thu: '08:00 - 16:00', Fri: 'Off' },
        leave: { allowance: 25, taken: 5, sick: 0, pending: 2, nationalHolidays: 2 },
        // ADDED: Past Reviews
        reviews: [
            { id: 201, date: '2025-01-10', status: 'Completed', type: 'Probation', rating: 7, manager: 'Support_Manager' },
            { id: 202, date: '2025-12-05', status: 'Completed', type: 'Monthly', rating: 8, manager: 'Support_Manager' }
        ]
    },
    { 
        id: 'ST-003', name: 'John Matrix', role: 'Support_Manager', 
        metrics: { tickets: 20, kyc: 5, csat: 4.9 },
        shifts: { Mon: '09:00 - 17:00', Tue: '09:00 - 17:00', Wed: '09:00 - 17:00', Thu: '09:00 - 17:00', Fri: '09:00 - 17:00' },
        leave: { allowance: 30, taken: 15, sick: 2, pending: 0, nationalHolidays: 2 },
        reviews: []
    },
    { 
        id: 'ST-004', name: 'Alex Hybrid', role: 'Hybrid_L1', 
        metrics: { sales: 8, tickets: 85, csat: 4.7 },
        shifts: { Mon: '09:00 - 17:00', Tue: '09:00 - 17:00', Wed: '12:00 - 20:00', Thu: '12:00 - 20:00', Fri: '09:00 - 17:00' },
        leave: { allowance: 25, taken: 10, sick: 0, pending: 0, nationalHolidays: 2 },
        // ADDED: Past Reviews
        reviews: [
            { id: 301, date: '2025-11-20', status: 'Completed', type: 'Quarterly', rating: 8, manager: 'Support_Manager' },
            { id: 302, date: '2025-12-20', status: 'Completed', type: 'Monthly', rating: 9, manager: 'Sales_Manager' }
        ]
    }
];

const INITIAL_RULES = [
    // --- DIRECT COMMISSIONS (Instant Pay) ---
    { id: 1, trigger: 'Pro Membership Sale', ruleKey: 'comm_pro', amount: 4.00, type: 'Commission' },
    { id: 2, trigger: 'Elite Membership Sale', ruleKey: 'comm_elite', amount: 6.00, type: 'Commission' },
    
    // --- SALES POT (Activity Basket) ---
    { id: 3, trigger: 'Client Funded Account', ruleKey: 'pot_funded', amount: 10.00, type: 'Sales Pot' },
    { id: 4, trigger: 'Active Trader Activation', ruleKey: 'pot_trade', amount: 5.00, type: 'Sales Pot' },

    // --- SERVICE POT (Activity Basket) ---
    { id: 5, trigger: 'Ticket Resolution', ruleKey: 'pot_ticket', amount: 0.50, type: 'Service Pot' },
    { id: 6, trigger: '5-Star Review', ruleKey: 'pot_review', amount: 2.00, type: 'Service Pot' },
    { id: 7, trigger: 'Manual KYC Approval', ruleKey: 'pot_kyc', amount: 1.00, type: 'Service Pot' },
    
    // --- EDUCATION BONUSES ---
    { id: 8, trigger: 'Risk Mgmt Course Completion', ruleKey: 'edu_risk', amount: 10.00, type: 'Education' }
];

// 2. ADD NEW MOCK DATA
const STAFF_LOGS = [
    { id: 1, staff: 'Sarah Connor', action: 'Closed Ticket #882', time: '10:42 AM', target: 'Resolution < 2h' },
    { id: 2, staff: 'Kyle Reese', action: 'Flagged Transaction', time: '10:15 AM', target: 'Accuracy 100%' },
    { id: 3, staff: 'Sarah Connor', action: 'Upgraded Client', time: '09:30 AM', target: 'Retention' },
    // Added Hybrid logs so Alex sees data
    { id: 4, staff: 'Alex Hybrid', action: 'Completed KYC Review', time: '11:00 AM', target: 'Speed < 1h' },
    { id: 5, staff: 'Alex Hybrid', action: 'Call with Client #990', time: '11:15 AM', target: 'Engagement' },
];

const MOCK_REVIEW_HISTORY = [
    { id: 101, staff: 'Sarah Connor', date: '2025-11-01', period: 'Monthly', rating: 9, manager: 'John Connor', signed: true },
    { id: 102, staff: 'Kyle Reese', date: '2025-10-01', period: 'Quarterly', rating: 7, manager: 'John Connor', signed: true },
];

// --- SUB-COMPONENTS ---

// 1. PAY TAB (Payslips & Bonus) - FIXED PERMISSIONS
const BonusTab = ({ role, rules, setRules, staff }) => { 
    // PERMISSION 1: Manager Access (For Bonus Rules Section)
    // Managers AND Admin can see/edit the rules table
    const isManagerAccess = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);
    
    // PERMISSION 2: System Admin (For File Uploads)
    // STRICTLY 'Admin' only. Managers CANNOT see this.
    const isSystemAdmin = role === 'Admin';

    // --- 1. IDENTIFY CURRENT USER ID (For Mock Data Privacy) ---
    let myId = 'ST-001'; 
    if (role === 'Support_L1') myId = 'ST-002';
    if (role === 'Support_Manager') myId = 'ST-003'; 
    if (role === 'Hybrid_L1') myId = 'ST-004';
    if (role === 'Sales_Manager') myId = 'ST-005';
    if (role === 'Admin') myId = 'ADMIN';

    // --- PAYSLIP STATE ---
    const [payslips, setPayslips] = useState([
        { id: 1, staffId: 'ST-001', name: 'Sarah Connor', period: 'December 2025', amount: '2,850.00', date: '2025-12-28' },
        { id: 2, staffId: 'ST-002', name: 'Kyle Reese', period: 'December 2025', amount: '2,200.00', date: '2025-12-28' },
        { id: 3, staffId: 'ST-004', name: 'Alex Hybrid', period: 'December 2025', amount: '2,542.60', date: '2025-12-28' },
        { id: 4, staffId: 'ST-003', name: 'John Matrix', period: 'December 2025', amount: '3,500.00', date: '2025-12-28' },
    ]);

    const [uploadData, setUploadData] = useState({ staffId: '', period: '', file: null });

    // --- BONUS RULES STATE ---
    const [newRule, setNewRule] = useState({ trigger: '', amount: '', type: 'Fixed' });
    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    // --- HANDLERS ---
    const handleUploadPayslip = () => {
        if (!uploadData.staffId || !uploadData.period) return alert("Please select staff and period");
        const selectedStaffMember = staff.find(s => s.id === uploadData.staffId);
        
        const newSlip = {
            id: Date.now(),
            staffId: uploadData.staffId,
            name: selectedStaffMember?.name || 'Staff',
            period: uploadData.period,
            amount: '---', 
            date: new Date().toISOString().split('T')[0]
        };

        setPayslips([...payslips, newSlip]);
        setUploadData({ staffId: '', period: '', file: null });
        alert(`Successfully uploaded payslip for ${selectedStaffMember?.name || 'Staff Member'}.`);
    };

    const handleDownload = (fileName) => {
        alert(`Downloading your payslip: ${fileName}...`);
    };

    const handleAdd = () => {
        if (newRule.trigger && newRule.amount) {
            setRules([...rules, { id: Date.now(), ruleKey: 'custom', ...newRule, amount: Number(newRule.amount) }]);
            setNewRule({ trigger: '', amount: '', type: 'Fixed' }); 
        }
    };
    const handleDelete = (id) => setRules(rules.filter(r => r.id !== id));
    const handleEditClick = (rule) => { setEditingId(rule.id); setEditFormData(rule); };
    const handleCancelClick = () => { setEditingId(null); setEditFormData({}); };
    const handleSaveClick = () => {
        const updatedRules = rules.map((rule) => rule.id === editingId ? { ...editFormData, amount: Number(editFormData.amount) } : rule);
        setRules(updatedRules);
        setEditingId(null);
    };

    // --- FILTER LOGIC ---
    // If Admin: See ALL payslips.
    // If Manager or Agent: See ONLY their own.
    const myPayslips = isSystemAdmin ? payslips : payslips.filter(p => p.staffId === myId);

    return (
        <div className="space-y-8 animate-fade-in">
            
            {/* 1. SUPER WIDGET (Agents Only) */}
            {!isManagerAccess && (
                <div className="h-64 w-full">
                    <HybridUnifiedWidget role={role} />
                </div>
            )}

            {/* 2. PAYSLIPS SECTION */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden p-6">
                <div className="flex justify-between items-center mb-6">
                    {/* Header changes based on permission */}
                    <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <FileText className="text-blue-400" /> {isSystemAdmin ? 'Payroll Management' : 'My Payslips'}
                    </h3>
                </div>

                {/* UPLOAD AREA - STRICTLY ADMIN ONLY (Hidden for Managers) */}
                {isSystemAdmin && (
                    <div className="bg-slate-950 p-4 rounded-lg border border-slate-800 mb-6">
                        <p className="text-xs font-bold text-blue-400 uppercase mb-3 flex items-center gap-2">
                             <Settings size={12} /> Payroll Management (Upload)
                        </p>
                        <div className="flex flex-col md:flex-row gap-4 items-end">
                            <div className="flex-1 w-full">
                                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Staff Member</label>
                                <select 
                                    className="w-full bg-slate-800 text-white p-2 rounded border border-slate-700 outline-none text-sm"
                                    value={uploadData.staffId}
                                    onChange={e => setUploadData({...uploadData, staffId: e.target.value})}
                                >
                                    <option value="">Select Agent...</option>
                                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="flex-1 w-full">
                                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">Period / Month</label>
                                <input 
                                    type="month" 
                                    className="w-full bg-slate-800 text-white p-2 rounded border border-slate-700 outline-none text-sm"
                                    value={uploadData.period}
                                    onChange={e => setUploadData({...uploadData, period: e.target.value})}
                                />
                            </div>
                            <div className="flex-1 w-full">
                                <label className="text-xs font-bold text-slate-500 uppercase mb-1 block">File</label>
                                <input type="file" className="text-xs text-slate-400" />
                            </div>
                            <button 
                                onClick={handleUploadPayslip}
                                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-6 rounded transition-colors flex items-center gap-2 h-[38px]"
                            >
                                <Plus size={16} /> Upload
                            </button>
                        </div>
                    </div>
                )}

                {/* PAYSLIP LIST */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {myPayslips.length > 0 ? myPayslips.map(slip => (
                        <div key={slip.id} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700 flex justify-between items-center group hover:border-blue-500/50 transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="p-3 bg-slate-800 rounded-lg text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                                    <FileText size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-white">{slip.period}</p>
                                    <p className="text-xs text-slate-400">
                                        {isSystemAdmin ? `${slip.name}` : `Available: ${slip.date}`}
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => handleDownload(`Payslip_${slip.period}.pdf`)}
                                className="text-slate-500 hover:text-white transition-colors p-2 rounded hover:bg-slate-700" 
                                title="Download PDF"
                            >
                                <Download size={18} />
                            </button>
                        </div>
                    )) : (
                        <div className="col-span-3 text-center py-8 border-2 border-dashed border-slate-800 rounded-xl">
                            <FileText size={32} className="mx-auto text-slate-600 mb-2"/>
                            <p className="text-slate-500 text-sm">No payslips found.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* 3. BONUS RULES SECTION (Visible to Managers & Admins) */}
            <div className="flex justify-between items-center pt-4 border-t border-slate-800">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <DollarSign className="text-emerald-400" /> Bonus Structure
                </h3>
            </div>

            {isManagerAccess ? (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-950 text-slate-500 uppercase font-bold text-xs">
                            <tr>
                                <th className="p-4">Trigger Event</th>
                                <th className="p-4">Reward Type</th>
                                <th className="p-4">Amount (€)</th>
                                <th className="p-4 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800 text-slate-300">
                            {rules.map(rule => (
                                <tr key={rule.id} className="group hover:bg-slate-800/30">
                                    {editingId === rule.id ? (
                                        <>
                                            <td className="p-4"><input value={editFormData.trigger} onChange={(e) => setEditFormData({...editFormData, trigger: e.target.value})} className="bg-slate-800 text-white p-1 rounded w-full border border-blue-500 outline-none" /></td>
                                            <td className="p-4">
                                                <select value={editFormData.type} onChange={(e) => setEditFormData({...editFormData, type: e.target.value})} className="bg-slate-800 text-white p-1 rounded border border-blue-500 outline-none">
                                                    <option>Commission</option><option>Sales Pot</option><option>Service Pot</option><option>Education</option>
                                                </select>
                                            </td>
                                            <td className="p-4"><input type="number" value={editFormData.amount} onChange={(e) => setEditFormData({...editFormData, amount: e.target.value})} className="bg-slate-800 text-white p-1 rounded w-24 border border-blue-500 outline-none" /></td>
                                            <td className="p-4 text-right flex justify-end gap-2">
                                                <button onClick={handleSaveClick} className="text-green-400 hover:bg-green-500/20 p-1 rounded transition-colors"><CheckCircle size={18} /></button>
                                                <button onClick={handleCancelClick} className="text-slate-400 hover:bg-slate-700 p-1 rounded transition-colors"><XCircle size={18} /></button>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td className="p-4 font-bold">{rule.trigger}</td>
                                            <td className="p-4"><span className={`px-2 py-1 rounded text-xs border ${rule.type === 'Commission' ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : rule.type.includes('Pot') ? 'bg-purple-500/10 border-purple-500/30 text-purple-400' : 'bg-slate-800 border-slate-700'}`}>{rule.type}</span></td>
                                            <td className="p-4 text-emerald-400 font-bold font-mono">€{Number(rule.amount).toFixed(2)}</td>
                                            <td className="p-4 text-right flex justify-end gap-3 opacity-50 group-hover:opacity-100 transition-opacity">
                                                <button onClick={() => handleEditClick(rule)} className="text-blue-400 hover:text-white transition-colors"><Edit2 size={16} /></button>
                                                <button onClick={() => handleDelete(rule.id)} className="text-slate-600 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                            <tr className="bg-slate-800/20 border-t border-slate-700">
                                <td className="p-4"><input placeholder="Add new trigger..." value={newRule.trigger} onChange={e => setNewRule({...newRule, trigger: e.target.value})} className="bg-transparent border-b border-slate-600 w-full focus:outline-none focus:border-blue-500 text-xs py-1" /></td>
                                <td className="p-4"><select value={newRule.type} onChange={e => setNewRule({...newRule, type: e.target.value})} className="bg-slate-800 text-xs border border-slate-600 rounded p-1 outline-none text-slate-300"><option>Commission</option><option>Sales Pot</option><option>Service Pot</option><option>Education</option></select></td>
                                <td className="p-4"><input type="number" placeholder="0.00" value={newRule.amount} onChange={e => setNewRule({...newRule, amount: e.target.value})} className="bg-transparent border-b border-slate-600 w-20 focus:outline-none focus:border-blue-500 text-xs py-1" /></td>
                                <td className="p-4 text-right"><button onClick={handleAdd} className="bg-blue-600 hover:bg-blue-500 text-white p-1.5 rounded transition-colors shadow-lg shadow-blue-900/20"><Plus size={16}/></button></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rules.map(rule => (
                        <div key={rule.id} className="bg-slate-800/50 p-4 rounded-xl border border-slate-700 flex justify-between items-center hover:border-emerald-500/30 transition-colors">
                            <div><p className="text-sm font-bold text-white">{rule.trigger}</p><p className="text-xs text-slate-500">{rule.type} Bonus</p></div>
                            <span className="text-xl font-mono font-bold text-emerald-400">+€{Number(rule.amount).toFixed(2)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// 2. ROTA TAB (Updated Time Scales & Attendance)
const RotaTab = ({ staff, setStaff, role }) => {
    const isAdmin = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);
    const shiftTypes = ['Morning', 'Afternoon', 'Night', 'Off'];
    const shiftIcons = { 
        Morning: <Sunrise size={14} className="text-amber-400"/>, 
        Afternoon: <Sun size={14} className="text-orange-500"/>, 
        Night: <Moon size={14} className="text-indigo-400"/>, 
        Off: <XCircle size={14} className="text-slate-600"/> 
    };

    // Cycle Shift Logic
    const handleShiftClick = (staffId, day, currentShift) => {
        if (!isAdmin) return; // Only managers can edit
        const currentIndex = shiftTypes.indexOf(currentShift);
        const nextShift = shiftTypes[(currentIndex + 1) % shiftTypes.length];

        const updatedStaff = staff.map(emp => {
            if (emp.id === staffId) {
                return { ...emp, shifts: { ...emp.shifts, [day]: nextShift } };
            }
            return emp;
        });
        setStaff(updatedStaff);
    };

    // --- ATTENDANCE INCIDENT STATE ---
    const [incidents, setIncidents] = useState([
        { id: 1, staffId: 'ST-002', name: 'Kyle Reese', type: 'Late Arrival', date: '2025-10-24', reason: 'Traffic on M1', loggedBy: 'Admin' },
        { id: 2, staffId: 'ST-003', name: 'John Matrix', type: 'Left Early (Sickness)', date: '2025-10-22', reason: 'Migraine', loggedBy: 'Support_Manager' },
    ]);

    const [newLog, setNewLog] = useState({ staffId: '', type: 'Late Arrival', date: new Date().toISOString().split('T')[0], reason: '' });

    const handleLogIncident = () => {
        if (!newLog.staffId || !newLog.reason) return alert("Please select staff and enter a reason.");
        const staffMember = staff.find(s => s.id === newLog.staffId);
        
        const entry = {
            id: Date.now(),
            staffId: newLog.staffId,
            name: staffMember?.name || 'Unknown',
            type: newLog.type,
            date: newLog.date,
            reason: newLog.reason,
            loggedBy: role
        };
        setIncidents([entry, ...incidents]);
        setNewLog({ ...newLog, reason: '', staffId: '' });
    };
    
    const isManagerAccess = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);

    return (
        <div className="space-y-8 animate-fade-in"> 
            {/* ROTA BOX */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="p-4 bg-slate-950 border-b border-slate-800 flex justify-between items-center">
                    <h3 className="font-bold text-white">Weekly Rota (Click to Edit)</h3>
                    <div className="flex gap-4 text-xs text-slate-400 bg-slate-900/50 px-3 py-1.5 rounded-lg border border-slate-800">
                        <span className="flex items-center gap-1.5" title="Morning Shift">
                            <Sunrise size={14} className="text-amber-400"/> <span className="font-medium text-slate-300">09:00 - 17:00</span>
                        </span>
                        <span className="flex items-center gap-1.5" title="Afternoon Shift">
                            <Sun size={14} className="text-orange-500"/> <span className="font-medium text-slate-300">14:00 - 18:00</span>
                        </span>
                        <span className="flex items-center gap-1.5" title="Night Shift">
                            <Moon size={14} className="text-indigo-400"/> <span className="font-medium text-slate-300">17:00 - 00:00</span>
                        </span>
                    </div>
                </div>
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-800/50 text-slate-500 uppercase font-bold text-xs">
                        <tr>
                            <th className="p-4">Staff Member</th>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => <th key={d} className="p-4 text-center">{d}</th>)}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                        {staff.map(emp => (
                            <tr key={emp.id} className="hover:bg-slate-800/30">
                                <td className="p-4 font-bold text-white">{emp.name}</td>
                                {Object.entries(emp.shifts).map(([day, shift]) => (
                                    <td key={day} className="p-4 text-center">
                                        <button 
                                            onClick={() => handleShiftClick(emp.id, day, shift)}
                                            disabled={!isAdmin}
                                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border transition-all ${shift === 'Off' ? 'bg-slate-800 border-slate-700 text-slate-500' : 'bg-blue-900/20 border-blue-500/30 text-blue-300 hover:bg-blue-900/40'} ${isAdmin ? 'cursor-pointer hover:scale-105' : 'cursor-default'}`}
                                        >
                                            {shiftIcons[shift]} {shift}
                                        </button>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {isAdmin && <p className="text-[10px] text-slate-500 p-2 text-center">Tip: Click any shift cell to cycle through shift patterns.</p>}
            </div> 
       
            {/* MANAGER ATTENDANCE TRACKER */}
            {isManagerAccess && (
                <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden p-6 mt-12">
                    <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
                        <Clock className="text-amber-400" /> Attendance & Incident Log
                    </h3>
                    <div className="bg-slate-950 p-5 rounded-xl border border-slate-800 mb-8">
                        <h4 className="text-xs font-bold text-slate-500 uppercase mb-4">Log New Incident</h4>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div>
                                <label className="block text-xs font-bold text-slate-400 mb-1">Staff Member</label>
                                <select 
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white outline-none focus:border-blue-500"
                                    value={newLog.staffId}
                                    onChange={(e) => setNewLog({...newLog, staffId: e.target.value})}
                                >
                                    <option value="">Select Agent...</option>
                                    {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 mb-1">Incident Type</label>
                                <select 
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white outline-none focus:border-blue-500"
                                    value={newLog.type}
                                    onChange={(e) => setNewLog({...newLog, type: e.target.value})}
                                >
                                    <option value="Late Arrival">Late Arrival</option>
                                    <option value="Left Early (Sickness)">Left Early (Sickness)</option>
                                    <option value="Left Early (Family Emergency)">Left Early (Family Emergency)</option>
                                    <option value="Left Early (Parental Need)">Left Early (Parental Need)</option>
                                    <option value="Left Early (Other)">Left Early (Other)</option>
                                    <option value="Absent (No Show)">Absent (No Show)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-400 mb-1">Reason / Notes</label>
                                <input 
                                    type="text" 
                                    placeholder="e.g. Traffic, Childcare issue..."
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-sm text-white outline-none focus:border-blue-500"
                                    value={newLog.reason}
                                    onChange={(e) => setNewLog({...newLog, reason: e.target.value})}
                                />
                            </div>
                            <button 
                                onClick={handleLogIncident}
                                className="bg-amber-600 hover:bg-amber-500 text-white font-bold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                            >
                                <AlertCircle size={16} /> Log Incident
                            </button>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-950 text-slate-500 uppercase font-bold text-xs">
                                <tr>
                                    <th className="p-4 rounded-tl-lg">Staff Member</th>
                                    <th className="p-4">Incident Type</th>
                                    <th className="p-4">Date</th>
                                    <th className="p-4">Notes</th>
                                    <th className="p-4 rounded-tr-lg text-right">Logged By</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800 text-slate-300">
                                {incidents.map((inc) => (
                                    <tr key={inc.id} className="hover:bg-slate-800/30 transition-colors">
                                        <td className="p-4 font-bold text-white">{inc.name}</td>
                                        <td className="p-4">
                                            <span className={`text-xs px-2 py-1 rounded border font-bold ${
                                                inc.type.includes('Late') ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                                inc.type.includes('Sickness') ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                                                inc.type.includes('Absent') ? 'bg-red-900/20 text-red-500 border-red-500/30' :
                                                'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                            }`}>
                                                {inc.type}
                                            </span>
                                        </td>
                                        <td className="p-4 font-mono text-xs">{inc.date}</td>
                                        <td className="p-4 text-slate-400 italic">{inc.reason}</td>
                                        <td className="p-4 text-right text-xs text-slate-500 uppercase">{inc.loggedBy}</td>
                                    </tr>
                                ))}
                                {incidents.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="p-8 text-center text-slate-500 italic">No incidents recorded this month.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// --- SUB-COMPONENT 6: GRIEVANCE INBOX (Manager View) ---
const GrievanceInbox = () => {
    const [reports, setReports] = useState([
        { id: 'WB-992', type: 'Whistleblower', category: 'Fraud', date: '2025-10-24', subject: 'Suspicious transactions in VIP', status: 'New', severity: 'Critical', isAnon: true },
        { id: 'GR-104', type: 'Grievance', category: 'Interpersonal', date: '2025-10-22', subject: 'Dispute with Line Manager', status: 'Reviewing', severity: 'Medium', isAnon: false, author: 'Mike Tyson' },
    ]);

    return (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden mb-8 animate-fade-in">
            <div className="bg-slate-950 p-4 border-b border-slate-800 flex justify-between items-center">
                <h3 className="font-bold text-white flex items-center gap-2">
                    <ShieldAlert className="text-red-500" /> Compliance & Grievance Inbox
                </h3>
                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{reports.filter(r => r.status === 'New').length} New</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-900 text-slate-500 uppercase font-bold text-xs border-b border-slate-800">
                        <tr>
                            <th className="p-4">ID / Type</th>
                            <th className="p-4">Date</th>
                            <th className="p-4">Subject</th>
                            <th className="p-4">Submitted By</th>
                            <th className="p-4">Severity</th>
                            <th className="p-4 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 text-slate-300">
                        {reports.map((report) => (
                            <tr key={report.id} className="hover:bg-slate-800/50 transition-colors">
                                <td className="p-4">
                                    <span className="block font-mono text-xs text-slate-500 mb-1">{report.id}</span>
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${
                                        report.type === 'Whistleblower' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                    }`}>
                                        {report.type}
                                    </span>
                                </td>
                                <td className="p-4 text-xs font-mono">{report.date}</td>
                                <td className="p-4 font-bold text-white">{report.subject}</td>
                                <td className="p-4">
                                    {report.isAnon ? (
                                        <span className="flex items-center gap-1 text-slate-500 italic text-xs"><Lock size={12} /> Anonymous</span>
                                    ) : (
                                        <span className="text-white">{report.author}</span>
                                    )}
                                </td>
                                <td className="p-4">
                                    <span className={`text-[10px] uppercase font-bold ${report.severity === 'Critical' ? 'text-red-500' : 'text-amber-500'}`}>
                                        {report.severity}
                                    </span>
                                </td>
                                <td className="p-4 text-right">
                                    <button className="text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-3 py-1.5 rounded transition-colors">View Case</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT 5: GRIEVANCE & WHISTLEBLOWING HUB ---
const GrievanceSection = () => {
    const [whistleForm, setWhistleForm] = useState({ anonymous: true, category: 'Fraud/Theft', details: '' });
    const [complaintForm, setComplaintForm] = useState({ targetType: 'colleague', targetName: '', details: '' });

    const submitWhistle = () => {
        if (!whistleForm.details) return alert("Please provide details.");
        alert(`SECURE SUBMISSION: Your report has been encrypted and sent directly to the Legal & Compliance Board.`);
        setWhistleForm({ ...whistleForm, details: '' });
    };

    const submitComplaint = () => {
        if (!complaintForm.targetName || !complaintForm.details) return alert("Please complete all fields.");
        const destination = complaintForm.targetType === 'manager' ? 'HR Director' : 'Line Manager';
        alert(`GRIEVANCE FILED. \n\nReport routed securely to: ${destination}.`);
        setComplaintForm({ ...complaintForm, targetName: '', details: '' });
    };

    return (
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8 animate-fade-in">
            {/* 1. FORMAL COMPLAINT */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden flex flex-col">
                <div className="bg-slate-950 p-4 border-b border-slate-800">
                    <h3 className="font-bold text-white flex items-center gap-2"><FileText className="text-blue-500" /> Formal Grievance</h3>
                    <p className="text-xs text-slate-400 mt-1">For interpersonal or operational disputes.</p>
                </div>
                <div className="p-6 space-y-4 flex-1">
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Who is this regarding?</label>
                        <select className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500" value={complaintForm.targetType} onChange={(e) => setComplaintForm({...complaintForm, targetType: e.target.value})}>
                            <option value="colleague">A Colleague / Peer</option>
                            <option value="manager">My Manager / Supervisor</option>
                        </select>
                        <div className={`mt-2 text-xs p-2 rounded border ${complaintForm.targetType === 'manager' ? 'bg-purple-500/10 text-purple-300 border-purple-500/20' : 'bg-blue-500/10 text-blue-300 border-blue-500/20'}`}>
                            <span className="font-bold">Routing:</span> {complaintForm.targetType === 'manager' ? "This report will bypass your manager and go directly to HR." : "This report will be reviewed by your Line Manager."}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Name of Person Involved</label>
                        <input type="text" className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-blue-500" placeholder="Full Name" value={complaintForm.targetName} onChange={(e) => setComplaintForm({...complaintForm, targetName: e.target.value})} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-400 mb-1">Details of Incident</label>
                        <textarea className="w-full bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm text-white h-32 resize-none focus:outline-none focus:border-blue-500" placeholder="Please describe what happened..." value={complaintForm.details} onChange={(e) => setComplaintForm({...complaintForm, details: e.target.value})}></textarea>
                    </div>
                    <button onClick={submitComplaint} className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"><Send size={16} /> Submit Grievance</button>
                </div>
            </div>
            {/* 2. WHISTLEBLOWER */}
            <div className="bg-red-950/10 border border-red-900/30 rounded-xl overflow-hidden flex flex-col relative">
                <div className="absolute top-4 right-4 text-red-500/20 pointer-events-none"><ShieldAlert size={100} /></div>
                <div className="bg-red-950/30 p-4 border-b border-red-900/30">
                    <h3 className="font-bold text-red-400 flex items-center gap-2"><Lock className="text-red-500" /> Whistleblower Channel</h3>
                    <p className="text-xs text-red-300/70 mt-1">For illegal acts, fraud, or serious breaches.</p>
                </div>
                <div className="p-6 space-y-4 flex-1 relative z-10">
                    <div className="bg-red-950/40 border border-red-500/20 p-3 rounded-lg flex items-start gap-3">
                        <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={16} />
                        <p className="text-[10px] text-red-200 leading-relaxed"><strong className="block text-red-400 uppercase mb-1">Strictly Confidential</strong>Reports submitted here are encrypted and sent directly to the Legal & Compliance Board. Your Line Manager and HR will <u>not</u> have access to this data.</p>
                    </div>
                    <div className="flex items-center justify-between bg-slate-900/50 p-3 rounded-lg border border-red-500/10">
                        <span className="text-xs font-bold text-slate-400">Submit Anonymously?</span>
                        <button onClick={() => setWhistleForm({...whistleForm, anonymous: !whistleForm.anonymous})} className={`w-12 h-6 rounded-full p-1 transition-colors ${whistleForm.anonymous ? 'bg-green-500' : 'bg-slate-700'}`}>
                            <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${whistleForm.anonymous ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-red-400/70 mb-1">Violation Category</label>
                        <select className="w-full bg-slate-900/50 border border-red-900/30 rounded-lg p-3 text-sm text-slate-200 focus:outline-none focus:border-red-500" value={whistleForm.category} onChange={(e) => setWhistleForm({...whistleForm, category: e.target.value})}>
                            <option>Fraud / Theft / Financial Crime</option>
                            <option>Data Breach / Security Violation</option>
                            <option>Discrimination / Harassment</option>
                            <option>Health & Safety Danger</option>
                            <option>Other Legal Violation</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-red-400/70 mb-1">Evidence / Details</label>
                        <textarea className="w-full bg-slate-900/50 border border-red-900/30 rounded-lg p-3 text-sm text-slate-200 h-32 resize-none focus:outline-none focus:border-red-500" placeholder="Describe the violation in detail..." value={whistleForm.details} onChange={(e) => setWhistleForm({...whistleForm, details: e.target.value})}></textarea>
                    </div>
                    <button onClick={submitWhistle} className="w-full py-3 bg-red-700 hover:bg-red-600 text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-900/20"><Lock size={16} /> Secure Submission</button>
                </div>
            </div>
        </div>
    );
};

// 3. HR TAB (Updated)
const HRTab = ({ staff, setStaff, role, currentUser }) => {
    const isAdmin = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);
    const [requestType, setRequestType] = useState('Holiday'); 
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [activeAction, setActiveAction] = useState(null);
    const [actionDate, setActionDate] = useState('');
    const [actionTime, setActionTime] = useState('14:00');

    let myId = 'ST-001'; 
    if (currentUser?.id) myId = currentUser.id;
    else {
        if (role === 'Support_L1') myId = 'ST-002'; 
        if (role === 'Support_Manager') myId = 'ST-003'; 
        if (role === 'Hybrid_L1') myId = 'ST-004';
    }

    const handleSubmitRequest = () => {
        const updatedStaff = staff.map(emp => {
            if (emp.id === myId) {
                const isSick = requestType === 'Sick';
                const dateString = isSick ? new Date().toISOString().split('T')[0] : `${dateRange.start} to ${dateRange.end}`;
                return { ...emp, leave: { ...emp.leave, pending: isSick ? 0 : emp.leave.pending + 1, sick: isSick ? emp.leave.sick + 1 : emp.leave.sick, lastRequest: isSick ? 'Sick Day Reported' : `Holiday: ${dateString}` } };
            }
            return emp;
        });
        setStaff(updatedStaff);
        setDateRange({ start: '', end: '' });
        alert(requestType === 'Sick' ? "Get well soon! Manager notified." : "Holiday request submitted for approval.");
    };

    const handleApproveLeave = (staffId) => {
        const updatedStaff = staff.map(emp => {
            if (emp.id === staffId && emp.leave.pending > 0) return { ...emp, leave: { ...emp.leave, taken: emp.leave.taken + emp.leave.pending, pending: 0 } };
            return emp;
        });
        setStaff(updatedStaff);
    };

    const handleSaveDate = (staffId) => {
        if (!actionDate) return;
        const updatedStaff = staff.map(emp => {
            if (emp.id === staffId) {
                let updatedReviews = [...emp.reviews];
                const dateTimeString = `${actionDate} at ${actionTime}`;
                if (activeAction.type === 'book') updatedReviews.push({ id: Date.now(), date: dateTimeString, status: 'Scheduled', type: 'Performance' });
                else if (activeAction.type === 'reschedule') updatedReviews = updatedReviews.map(r => (r.status === 'Scheduled' || r.status === 'Rescheduling') ? { ...r, date: dateTimeString, status: 'Rescheduled' } : r);
                return { ...emp, reviews: updatedReviews };
            }
            return emp;
        });
        setStaff(updatedStaff);
        setActiveAction(null); setActionDate('');    
    };

    const handleConfirm = (staffId, reviewId) => {
        const updatedStaff = staff.map(emp => {
            if (emp.id === staffId) {
                const updatedReviews = emp.reviews.map(r => r.id === reviewId ? { ...r, status: 'Confirmed' } : r);
                return { ...emp, reviews: updatedReviews };
            }
            return emp;
        });
        setStaff(updatedStaff);
    };

    return (
        <div className="space-y-8 animate-fade-in">
            {isAdmin && <GrievanceInbox />}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                    <div className="bg-slate-900 p-6 rounded-xl border border-slate-800">
                         <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Briefcase size={20} className="text-blue-500"/> Request Time Off</h3>
                        <div className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                            <div className="flex gap-2 mb-4">
                                <button onClick={() => setRequestType('Holiday')} className={`flex-1 py-2 text-xs font-bold rounded transition-colors ${requestType === 'Holiday' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-400 hover:text-white'}`}>Holiday</button>
                                <button onClick={() => setRequestType('Sick')} className={`flex-1 py-2 text-xs font-bold rounded transition-colors ${requestType === 'Sick' ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-slate-700 text-slate-400 hover:text-white'}`}>I'm Sick Today</button>
                            </div>
                            {requestType === 'Holiday' ? (
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        <div className="flex-1"><label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">From</label><input type="date" value={dateRange.start} onChange={(e) => setDateRange({...dateRange, start: e.target.value})} className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500" /></div>
                                        <div className="flex-1"><label className="text-[10px] text-slate-500 font-bold uppercase block mb-1">To</label><input type="date" value={dateRange.end} onChange={(e) => setDateRange({...dateRange, end: e.target.value})} className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-1.5 text-xs text-white focus:outline-none focus:border-blue-500" /></div>
                                    </div>
                                    <button onClick={handleSubmitRequest} disabled={!dateRange.start || !dateRange.end} className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-bold py-2 rounded mt-2">Submit Request</button>
                                </div>
                            ) : (
                                <div className="text-center">
                                    <p className="text-xs text-slate-400 mb-3">Not feeling well? Notify your manager immediately.</p>
                                    <button onClick={handleSubmitRequest} className="w-full bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-2 rounded flex items-center justify-center gap-2 transition-colors"><AlertCircle size={16} /> Report Sick Absence</button>
                                </div>
                            )}
                        </div>
                    </div>
                    {isAdmin && (
                        <div className="bg-slate-900 p-6 rounded-xl border border-slate-800">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Users size={20} className="text-slate-400"/> Team Allowance</h3>
                            <div className="space-y-4">
                                {staff.map(emp => {
                                    const remaining = emp.leave.allowance - emp.leave.taken - emp.leave.nationalHolidays;
                                    return (
                                        <div key={emp.id} className="bg-slate-800/50 p-4 rounded-lg border border-slate-700">
                                            <div className="flex justify-between mb-2">
                                                <div className="flex items-center gap-2"><span className="font-bold text-white">{emp.name}</span>{emp.leave.lastRequest && emp.leave.lastRequest.includes('Sick') && <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-bold uppercase">Sick</span>}</div>
                                                <span className={`text-xs font-bold px-2 py-0.5 rounded ${remaining < 5 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{remaining} Days Left</span>
                                            </div>
                                            <div className="w-full bg-slate-800 h-2 rounded-full overflow-hidden flex">
                                                <div className="bg-blue-500 h-full" style={{ width: `${(emp.leave.taken / emp.leave.allowance) * 100}%` }}></div>
                                                <div className="bg-amber-500 h-full" style={{ width: `${(emp.leave.nationalHolidays / emp.leave.allowance) * 100}%` }}></div>
                                                <div className="bg-red-500 h-full" style={{ width: `${(emp.leave.sick / emp.leave.allowance) * 100}%` }}></div>
                                            </div>
                                            <div className="flex justify-between text-[10px] text-slate-500 mt-1"><span>Taken: {emp.leave.taken} | Sick: {emp.leave.sick}</span>{emp.leave.pending > 0 && <span className="text-blue-400 font-bold">Requesting: {emp.leave.pending} days</span>}</div>
                                            {emp.leave.pending > 0 && emp.id !== myId && (<div className="mt-3 flex gap-2 animate-fade-in"><button onClick={() => handleApproveLeave(emp.id)} className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs py-1.5 rounded font-bold transition-colors">Approve Request</button><button className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-xs py-1.5 rounded transition-colors">Decline</button></div>)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
                <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 h-fit">
                    <h3 className="font-bold text-white mb-4 flex items-center gap-2"><UserCheck size={20} className="text-purple-500"/> Performance Reviews</h3>
                    <div className="space-y-4">
                        {staff.length === 0 ? (<p className="text-slate-500 text-sm">No review data found for your role.</p>) : (
                            staff.map(emp => {
                                const activeReview = emp.reviews.find(r => ['Scheduled', 'Confirmed', 'Rescheduling', 'Rescheduled'].includes(r.status));
                                const isMyRow = activeAction?.id === emp.id;
                                return (
                                    <div key={emp.id} className="p-4 border border-slate-700 rounded-lg hover:border-purple-500/50 transition-colors">
                                        <div className="flex justify-between items-start"><div><p className="font-bold text-white text-sm">{emp.name}</p><p className="text-xs text-slate-400">Last Review: Oct 15</p></div>{activeReview ? (<span className={`text-[10px] font-bold px-2 py-1 rounded ${activeReview.status === 'Confirmed' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'}`}>{activeReview.status}</span>) : (<span className="text-[10px] text-slate-600 bg-slate-800 px-2 py-1 rounded">No Review Scheduled</span>)}</div>
                                        {isMyRow && (<div className="mt-3 pt-3 border-t border-slate-700 flex flex-col gap-2 animate-fade-in"><div className="flex gap-2"><input type="date" autoFocus value={actionDate} onChange={(e) => setActionDate(e.target.value)} className="bg-slate-800 text-white text-xs p-2 rounded border border-slate-600 outline-none w-full focus:border-blue-500" /><input type="time" value={actionTime} onChange={(e) => setActionTime(e.target.value)} className="bg-slate-800 text-white text-xs p-2 rounded border border-slate-600 outline-none w-24 focus:border-blue-500" /></div><div className="flex gap-2"><button onClick={() => handleSaveDate(emp.id)} className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs font-bold py-2 rounded">Save</button><button onClick={() => { setActiveAction(null); setActionDate(''); }} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-xs py-2 rounded">Cancel</button></div></div>)}
                                        {isAdmin && !activeReview && !isMyRow && (<div className="mt-3 pt-3 border-t border-slate-700"><button onClick={() => { setActiveAction({ id: emp.id, type: 'book' }); setActionDate(''); setActionTime('14:00'); }} className="w-full bg-blue-600/10 hover:bg-blue-600 hover:text-white text-blue-400 text-xs font-bold py-2 rounded transition-colors border border-blue-500/30">+ Book Review</button></div>)}
                                        {activeReview && !isMyRow && (<div className="mt-3 pt-3 border-t border-slate-700"><p className="text-xs text-white mb-2">Date: <span className="text-amber-400 font-mono">{activeReview.date}</span></p><div className="flex gap-2">{(activeReview.status === 'Scheduled' || activeReview.status === 'Rescheduled') && (<button onClick={() => handleConfirm(emp.id, activeReview.id)} className="flex-1 bg-green-600 hover:bg-green-500 text-white text-xs py-1.5 rounded font-bold">Confirm</button>)}<button onClick={() => { setActiveAction({ id: emp.id, type: 'reschedule' }); setActionDate(''); setActionTime('14:00'); }} className="flex-1 bg-slate-700 hover:bg-slate-600 text-white text-xs py-1.5 rounded font-bold">Reschedule</button></div></div>)}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>
            <GrievanceSection />
        </div>
    );
};
    
const OverviewTab = ({ staff, role }) => {
    // --- LIVE CALCULATIONS ---
    const totalStaff = staff.length;
    const pendingLeave = staff.reduce((acc, emp) => acc + (emp.leave.pending > 0 ? 1 : 0), 0);
    const onShiftToday = staff.filter(emp => emp.shifts['Mon'] !== 'Off').length;
    const avgScore = 4.8; 

    // Permission Check: Managers Only
    const isAdmin = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);

    // --- NEW: HR CREATION STATE ---
    const [showAddModal, setShowAddModal] = useState(false);
    const [newStaff, setNewStaff] = useState({ full_name: '', email: '', password: '', role: 'Support_L1' });
    const [isCreating, setIsCreating] = useState(false);

    const handleCreateStaff = async () => {
        if (!newStaff.full_name || !newStaff.email || !newStaff.password) return alert("Fill all fields");
        setIsCreating(true);
        try {
            const { error } = await supabase.functions.invoke('create-user', {
                body: newStaff
            });
            if (error) throw error;
            alert(`${newStaff.full_name} has been successfully added to the CRM!`);
            setShowAddModal(false);
            setNewStaff({ full_name: '', email: '', password: '', role: 'Support_L1' });
        } catch (err) {
            alert("Error creating user: " + err.message);
        } finally {
            setIsCreating(false);
        }
    };

    // Identify "Me" for Agents (to show personal stats)
    let myId = 'ST-001';
    if (role === 'Support_L1') myId = 'ST-002';
    if (role === 'Hybrid_L1') myId = 'ST-004';
    const myData = staff.find(s => s.id === myId) || staff[0];

    return (
        <div className="animate-fade-in">
            
            {/* --- MANAGER VIEW (Grid Layout - Unchanged) --- */}
            {isAdmin ? (
                <div className="space-y-6">
                    {/* Manager Vital Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-blue-600/20 text-blue-400 flex items-center justify-center"><Users size={24} /></div>
                            <div><p className="text-slate-500 text-xs font-bold uppercase">Total Staff</p><h4 className="text-2xl font-bold text-white">{totalStaff}</h4></div>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-emerald-600/20 text-emerald-400 flex items-center justify-center"><Briefcase size={24} /></div>
                            <div><p className="text-slate-500 text-xs font-bold uppercase">Active Shift</p><h4 className="text-2xl font-bold text-white">{onShiftToday} <span className="text-sm text-slate-500 font-normal">/ {totalStaff}</span></h4></div>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${pendingLeave > 0 ? 'bg-amber-500/20 text-amber-400 animate-pulse' : 'bg-slate-800 text-slate-500'}`}><AlertCircle size={24} /></div>
                            <div><p className="text-slate-500 text-xs font-bold uppercase">Pending Requests</p><h4 className="text-2xl font-bold text-white">{pendingLeave}</h4></div>
                        </div>
                        <div className="bg-slate-900 p-4 rounded-xl border border-slate-800 flex items-center gap-4">
                            <div className="w-12 h-12 rounded-lg bg-purple-600/20 text-purple-400 flex items-center justify-center"><TrendingUp size={24} /></div>
                            <div><p className="text-slate-500 text-xs font-bold uppercase">Avg Performance</p><h4 className="text-2xl font-bold text-white">{avgScore} <span className="text-sm text-slate-500">/ 5.0</span></h4></div>
                        </div>
                    </div>

                    {/* Manager Middle Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="font-bold text-white flex items-center gap-2"><Clock size={18} className="text-blue-500" /> Who is working today?</h3>
                                <span className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">Monday Schedule</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {staff.map(emp => (
                                    <div key={emp.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700/50">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-white">{emp.name.charAt(0)}</div>
                                            <div><p className="text-sm font-bold text-white">{emp.name}</p><p className="text-xs text-slate-500">{emp.role}</p></div>
                                        </div>
                                        <span className={`text-xs px-2 py-1 rounded font-bold ${emp.shifts.Mon === 'Off' ? 'bg-slate-700 text-slate-500' : 'bg-blue-900/30 text-blue-400'}`}>{emp.shifts.Mon}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 flex flex-col">
                            <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Settings size={18} className="text-slate-400" /> Quick Actions</h3>
                            <div className="space-y-3 flex-1">
                                {pendingLeave > 0 && (
                                    <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-3">
                                        <AlertCircle size={16} className="text-amber-500 mt-0.5" />
                                        <div><p className="text-sm font-bold text-amber-500">Leave Approval Needed</p><p className="text-xs text-slate-400 mt-1">{pendingLeave} staff members requested time off.</p></div>
                                    </div>
                                )}
                                <button onClick={() => setShowAddModal(true)} className="w-full text-left p-3 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 border border-transparent hover:border-slate-700 group">
                                    <div className="bg-blue-600 text-white p-2 rounded group-hover:bg-blue-500 transition-colors"><Plus size={16} /></div>
                                    <div><p className="text-sm font-bold text-white">Add Staff Member</p><p className="text-xs text-slate-500">Create new user account</p></div>
                                </button>

                                {/* ADD STAFF MODAL */}
                                {showAddModal && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4">
                                        <div className="bg-slate-900 border border-slate-700 w-full max-w-md rounded-2xl p-6 shadow-2xl">
                                            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><ShieldCheck className="text-blue-500" /> Secure Onboarding</h3>
                                            <div className="space-y-4">
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 mb-1">Full Name</label>
                                                    <input type="text" value={newStaff.full_name} onChange={(e) => setNewStaff({...newStaff, full_name: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white focus:border-blue-500 outline-none" placeholder="e.g. John Doe" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 mb-1">Work Email</label>
                                                    <input type="email" value={newStaff.email} onChange={(e) => setNewStaff({...newStaff, email: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white focus:border-blue-500 outline-none" placeholder="john@nommia.io" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 mb-1">Temporary Password</label>
                                                    <input type="password" value={newStaff.password} onChange={(e) => setNewStaff({...newStaff, password: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white focus:border-blue-500 outline-none" placeholder="••••••••" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-400 mb-1">Access Role</label>
                                                    <select value={newStaff.role} onChange={(e) => setNewStaff({...newStaff, role: e.target.value})} className="w-full bg-slate-950 border border-slate-800 rounded-lg p-2.5 text-sm text-white focus:border-blue-500 outline-none">
                                                        <option value="Support_L1">Support Agent (L1)</option>
                                                        <option value="Sales_L1">Sales Executive (L1)</option>
                                                        <option value="Hybrid_L1">Hybrid Agent (L1)</option>
                                                        <option value="Support_Manager">Support Manager</option>
                                                        <option value="Admin">System Admin</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="flex gap-3 mt-6">
                                                <button onClick={handleCreateStaff} disabled={isCreating} className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg transition-colors">{isCreating ? 'Creating...' : 'Create Account'}</button>
                                                <button onClick={() => setShowAddModal(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-2.5 rounded-lg transition-colors">Cancel</button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <button className="w-full text-left p-3 hover:bg-slate-800 rounded-lg transition-colors flex items-center gap-3 border border-transparent hover:border-slate-700 group">
                                    <div className="bg-purple-600 text-white p-2 rounded group-hover:bg-purple-500 transition-colors"><FileText size={16} /></div>
                                    <div><p className="text-sm font-bold text-white">Generate Payroll Report</p><p className="text-xs text-slate-500">Export CSV for finance</p></div>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                /* --- UNIFIED AGENT VIEW (Sales, Support, Hybrid) --- */
                /* Using a vertical flex stack ensures components ALWAYS take 100% width */
                <div className="flex flex-col gap-6 w-full">
                    
                    {/* 1. Personal Metrics Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                        <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                            <h3 className="text-slate-500 text-xs font-bold uppercase mb-2">
                                {myData.role.includes('Sales') ? 'Total Sales' : 'Tickets Closed'}
                            </h3>
                            <div className="flex justify-between items-end">
                                <div className="text-3xl font-bold text-white">
                                    {myData.metrics.sales || myData.metrics.tickets || 0}
                                </div>
                                <div className="text-xs text-green-400 font-bold flex items-center gap-1">
                                    <TrendingUp size={14} /> On Target
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                            <h3 className="text-slate-500 text-xs font-bold uppercase mb-2">
                                {myData.role.includes('Sales') ? 'Calls Made' : 'KYC Checks'}
                            </h3>
                            <div className="flex justify-between items-end">
                                <div className="text-3xl font-bold text-blue-400">
                                    {myData.metrics.calls || myData.metrics.kyc || 0}
                                </div>
                                <div className="text-xs text-slate-500">This Month</div>
                            </div>
                        </div>

                        <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                            <h3 className="text-slate-500 text-xs font-bold uppercase mb-2">Quality Score (CSAT)</h3>
                            <div className="flex justify-between items-end">
                                <div className="text-3xl font-bold text-amber-400">{myData.metrics.csat || 0.0}</div>
                                <div className="flex gap-0.5 pb-1">
                                    {[1,2,3,4,5].map(s => (
                                        <div key={s} className={`w-1.5 h-1.5 rounded-full ${s <= Math.round(myData.metrics.csat) ? 'bg-amber-400' : 'bg-slate-700'}`} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 2. SUPER WIDGET - FORCED FULL WIDTH */}
                    {/* We remove all grid logic and stick it in a full-width div */}
                    <div className="w-full bg-slate-900 rounded-2xl overflow-hidden border border-slate-800">
                         {/* Passing 'Hybrid' to role ensures it renders the full UI if specific roles were limited */}
                         <HybridUnifiedWidget role={role} /> 
                    </div>

                    {/* 3. Next Shift Info */}
                    <div className="w-full bg-slate-900 p-6 rounded-2xl border border-slate-800 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-blue-500/10 text-blue-400 rounded-xl">
                                <Clock size={24} />
                            </div>
                            <div>
                                <h3 className="text-white font-bold">Next Shift</h3>
                                <p className="text-slate-400 text-sm">Tomorrow • {myData.shifts.Tue || '09:00 - 17:00'}</p>
                            </div>
                        </div>
                        <button className="text-xs font-bold text-slate-500 hover:text-white transition-colors">
                            View Schedule &rarr;
                        </button>
                    </div>

                </div>
            )}
        </div>
    );
};

// --- SUB-COMPONENT 5: KPI DASHBOARD (Updated with AI Flag Rate) ---
const KPITab = ({ staff }) => {
    
    // 1. SMART FILTERING: Only show logs for the staff members currently visible
    const visibleLogs = STAFF_LOGS.filter(log => staff.some(person => person.name === log.staff));

    return (
        <div className="animate-fade-in space-y-8">
            
            {/* KPI Cards */}
            <div className={`grid grid-cols-1 ${staff.length > 1 ? 'lg:grid-cols-3' : ''} gap-6`}>
                {staff.map((emp, i) => {
                    const score = emp.metrics.sales ? (emp.metrics.sales * 5) : (emp.metrics.tickets / 2);
                    const status = score > 50 ? 'Top Performer' : 'Good';
                    const csat = emp.metrics.csat || 4.5;
                    
                    // MOCK AI FLAG RATE (Randomized for demo)
                    // Represents % of interactions flagged by AI for compliance review
                    const aiFlagRate = emp.metrics.aiFlag || Math.floor(Math.random() * 12); // 0% - 12%

                    return (
                        <div key={i} className="bg-slate-900 p-6 rounded-2xl border border-slate-800 relative overflow-hidden group hover:border-slate-700 transition-colors">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold border border-slate-600">
                                        {emp.name.charAt(0)}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-white text-lg leading-tight">{emp.name}</h3>
                                        <span className="text-xs text-slate-500 uppercase font-bold">{emp.role}</span>
                                    </div>
                                </div>
                                <div className={`px-3 py-1 rounded-full text-xs font-bold border ${status === 'Top Performer' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-blue-500/10 text-blue-400 border-blue-500/20'}`}>
                                    {status}
                                </div>
                            </div>

                            {/* Efficiency Section */}
                            <div className="mb-6 p-4 bg-slate-950/50 rounded-xl border border-slate-800/50">
                                <h4 className="text-[10px] text-slate-500 uppercase font-bold mb-3 flex items-center gap-2"><TrendingUp size={12}/> Efficiency Metrics</h4>
                                <div className="grid grid-cols-3 gap-2 text-center mb-4">
                                    {/* LOGIC: Check for Hybrid first, then Sales, then Support */}
                                    {emp.role.includes('Hybrid') ? (
                                        <>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.sales}</div><div className="text-[10px] text-slate-500">Sales</div></div>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.tickets}</div><div className="text-[10px] text-slate-500">Tickets</div></div>
                                            <div><div className="text-lg font-mono font-bold text-blue-400">{emp.metrics.csat}</div><div className="text-[10px] text-slate-500">CSAT</div></div>
                                        </>
                                    ) : emp.role.includes('Sales') ? (
                                        <>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.sales}</div><div className="text-[10px] text-slate-500">Sales</div></div>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.calls}</div><div className="text-[10px] text-slate-500">Calls</div></div>
                                            <div><div className="text-lg font-mono font-bold text-blue-400">{emp.metrics.csat}</div><div className="text-[10px] text-slate-500">CSAT</div></div>
                                        </>
                                    ) : (
                                        <>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.tickets || 0}</div><div className="text-[10px] text-slate-500">Tickets</div></div>
                                            <div><div className="text-lg font-mono font-bold text-white">{emp.metrics.kyc || 0}</div><div className="text-[10px] text-slate-500">KYC</div></div>
                                            <div><div className="text-lg font-mono font-bold text-blue-400">{emp.metrics.csat}</div><div className="text-[10px] text-slate-500">CSAT</div></div>
                                        </>
                                    )}
                                </div>

                                {/* AI FLAG RATE BAR */}
                                <div>
                                    <div className="flex justify-between text-[10px] uppercase font-bold text-slate-500 mb-1">
                                        <span>AI Flag Rate</span>
                                        <span className={aiFlagRate > 10 ? "text-red-400" : aiFlagRate > 5 ? "text-amber-400" : "text-green-400"}>{aiFlagRate}% Risk</span>
                                    </div>
                                    <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                        {/* Color Logic: Green (<5%), Amber (5-10%), Red (>10%) */}
                                        <div 
                                            className={`h-full rounded-full ${aiFlagRate <= 5 ? 'bg-green-500' : aiFlagRate <= 10 ? 'bg-amber-500' : 'bg-red-500'}`} 
                                            style={{ width: `${Math.min((aiFlagRate / 15) * 100, 100)}%` }} 
                                        ></div>
                                    </div>
                                </div>
                            </div>

                            {/* Quality Section */}
                            <div className="space-y-4">
                                <h4 className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-2"><UserCheck size={12}/> Quality Assurance</h4>
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-slate-400">Customer Satisfaction</span>
                                    <div className="flex items-center gap-1"><span className="text-white font-bold">{csat}</span><div className="flex">{[...Array(5)].map((_, i) => (<Star key={i} size={10} className={i < Math.round(csat) ? "text-amber-500 fill-amber-500" : "text-slate-700"} />))}</div></div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            
            {/* Live Feed */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                <h3 className="font-bold text-white mb-4 flex items-center gap-2"><Clock size={18} /> Live Quality Audit Log</h3>
                <div className="space-y-4">
                    {visibleLogs.length > 0 ? (
                        visibleLogs.map(log => (
                            <div key={log.id} className="flex items-center gap-4 p-3 bg-slate-950/50 rounded-lg border border-slate-800 hover:bg-slate-900 transition-colors">
                                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-400 border border-slate-700">{log.staff.split(' ')[0][0]}</div>
                                <div className="flex-1">
                                    <p className="text-sm text-white"><span className="font-bold text-blue-400">{log.staff}</span> <span className="text-slate-400"> performed </span><span className="font-medium text-slate-200">{log.action}</span></p>
                                    <p className="text-xs text-slate-500 mt-0.5">Target: {log.target}</p>
                                </div>
                                <div className="text-right"><span className="text-xs text-slate-500 font-mono block">{log.time}</span><span className="text-[10px] bg-green-500/10 text-green-500 px-1.5 py-0.5 rounded border border-green-500/20">Verified</span></div>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-slate-500 italic p-4 text-center">No recent audit logs available for your view.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- SUB-COMPONENT 6: APPRAISALS TAB (Functional Downloads) ---
const AppraisalsTab = ({ staff, role }) => {
    const [viewState, setViewState] = useState('list'); // 'list' | 'form' | 'history'
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [period, setPeriod] = useState('Monthly');
    const [viewingHistoryItem, setViewingHistoryItem] = useState(null);

    // Permission Check
    const isAdmin = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(role);

    // AUTO-REDIRECT FOR AGENTS
    useEffect(() => {
        if (!isAdmin && staff.length > 0) {
            setSelectedStaff(staff[0]);
            setViewState('history');
        }
    }, [isAdmin, staff]);

    // Form Data State
    const [formData, setFormData] = useState({
        rating: 0, managerComments: '', staffComments: '', 
        growth: '', training: '', pay: '', 
        smartS: '', smartM: '', smartA: '', smartR: '', smartT: '',
        managerPass: '', staffPass: ''
    });

    // --- NEW: GENERATE & DOWNLOAD FUNCTION ---
    const handleDownloadReport = (review, staffName, e) => {
        if (e) e.stopPropagation(); // Prevent opening modal if clicking download in list view

        // 1. Construct the Report Content
        const content = `
NOMMIA CRM - PERFORMANCE APPRAISAL
================================================
STAFF MEMBER:   ${staffName}
REVIEW DATE:    ${review.date}
REVIEW TYPE:    ${review.type}
------------------------------------------------
OVERALL RATING: ${review.rating} / 10
MANAGER:        ${review.manager || 'System Admin'}
STATUS:         ${review.status}
------------------------------------------------
SUMMARY:
${review.rating >= 8 ? "Exceptional performance. Exceeded targets significantly." : 
  review.rating >= 5 ? "Solid performance. Targets met consistently." : 
  "Performance below expectations. Improvement plan required."}

------------------------------------------------
CONFIDENTIAL RECORD
Generated via Nommia CRM Team Hub
        `;

        // 2. Create a Blob (File Object)
        const blob = new Blob([content], { type: 'text/plain' });
        
        // 3. Create a temporary download link and click it
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Naming it .txt so it opens easily in browser/notepad. 
        // In a real app with 'jspdf', you would name this .pdf
        a.download = `Appraisal_${staffName.replace(/\s+/g, '_')}_${review.date}.txt`; 
        document.body.appendChild(a);
        a.click();
        
        // 4. Cleanup
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleStartReview = (emp) => {
        if (!isAdmin) return;
        setSelectedStaff(emp);
        setViewState('form');
        setFormData({ rating: 0, managerComments: '', staffComments: '', growth: '', training: '', pay: '', smartS: '', smartM: '', smartA: '', smartR: '', smartT: '', managerPass: '', staffPass: '' });
    };

    const handleViewHistory = (emp) => {
        setSelectedStaff(emp);
        setViewState('history');
    }

    return (
        <div className="animate-fade-in relative">
            
            {/* --- PAST REVIEW POP-UP MODAL --- */}
            {viewingHistoryItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
                    <div className="bg-slate-900 border border-slate-700 w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        
                        {/* Modal Header */}
                        <div className="p-6 border-b border-slate-800 flex justify-between items-start bg-slate-900">
                            <div>
                                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                    <ClipboardList className="text-blue-500" /> {viewingHistoryItem.type} Review
                                </h3>
                                <p className="text-sm text-slate-400 mt-1">
                                    Conducted on <span className="text-white font-mono">{viewingHistoryItem.date}</span>
                                </p>
                            </div>
                            <button 
                                onClick={() => setViewingHistoryItem(null)}
                                className="text-slate-500 hover:text-white hover:bg-slate-800 p-2 rounded-lg transition-colors"
                            >
                                <XCircle size={24} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-8 overflow-y-auto space-y-6 custom-scrollbar">
                            <div className="flex items-center gap-6 bg-slate-950 p-4 rounded-xl border border-slate-800">
                                <div className="text-center px-4 border-r border-slate-800">
                                    <span className="text-xs text-slate-500 font-bold uppercase">Overall Score</span>
                                    <div className={`text-4xl font-bold mt-1 ${viewingHistoryItem.rating >= 8 ? 'text-green-400' : viewingHistoryItem.rating >= 5 ? 'text-amber-400' : 'text-red-400'}`}>
                                        {viewingHistoryItem.rating}<span className="text-lg text-slate-600">/10</span>
                                    </div>
                                </div>
                                <div>
                                    <h4 className="font-bold text-white text-sm mb-1">Performance Summary</h4>
                                    <p className="text-xs text-slate-400 leading-relaxed">
                                        {viewingHistoryItem.rating >= 9 ? "Exceptional performance across all KPIs. Staff member demonstrated leadership qualities and exceeded sales/service targets significantly." : 
                                         viewingHistoryItem.rating >= 7 ? "Solid performance. Targets were met consistently. Quality assurance scores are stable, though minor improvements in efficiency are possible." : 
                                         "Performance below expectations. Specific improvement plan required for attendance and adherence to script protocols."}
                                    </p>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-blue-400 uppercase mb-3 flex items-center gap-2">
                                    <UserCheck size={14} /> Manager's Evaluation
                                </h4>
                                <div className="bg-slate-800/30 p-4 rounded-lg border border-slate-800 text-sm text-slate-300 leading-relaxed">
                                    "Overall, a {viewingHistoryItem.rating >= 8 ? 'fantastic' : 'productive'} period. 
                                    {selectedStaff.role.includes('Sales') ? ' Sales figures were strong, particularly in the Pro tier.' : ' Ticket resolution times have improved.'}
                                    I particularly appreciated the effort put into the new compliance training modules.
                                    Moving forward, let's focus on {selectedStaff.role.includes('Sales') ? 'increasing average call duration to build stronger rapport.' : 'reducing escalation rates for Tier 1 tickets.'}"
                                </div>
                            </div>

                            <div>
                                <h4 className="text-xs font-bold text-purple-400 uppercase mb-3 flex items-center gap-2">
                                    <Users size={14} /> Staff Feedback
                                </h4>
                                <div className="bg-slate-800/30 p-4 rounded-lg border border-slate-800 text-sm text-slate-300 italic leading-relaxed">
                                    "Thank you for the feedback. I agree with the points raised regarding {selectedStaff.role.includes('Sales') ? 'call duration' : 'escalations'}.
                                    I felt supported during this period and the new tools are helping with my workflow."
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 border-t border-slate-800 bg-slate-900 flex justify-between items-center">
                            <div className="flex items-center gap-2 text-xs text-green-500 bg-green-900/10 px-3 py-1.5 rounded-full border border-green-900/20">
                                <ShieldCheck size={14} /> Digitally Signed & Archived
                            </div>
                            
                            {/* POP-UP DOWNLOAD BUTTON - CONNECTED */}
                            <button 
                                onClick={(e) => handleDownloadReport(viewingHistoryItem, selectedStaff.name, e)}
                                className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold px-6 py-2 rounded-lg transition-colors flex items-center gap-2"
                            >
                                <Download size={16} /> Download PDF
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* VIEW 1: SELECTION LIST (Managers Only) */}
            {viewState === 'list' && (
                <div className="space-y-6">
                    <div className="flex justify-end bg-slate-900 p-1 rounded-lg border border-slate-800 w-fit ml-auto">
                        {['Monthly', 'Quarterly', 'Annual'].map(p => (
                            <button key={p} onClick={() => setPeriod(p)} className={`px-4 py-1.5 rounded text-xs font-bold transition-all ${period === p ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-300'}`}>{p}</button>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {staff.map((emp, i) => (
                            <div key={i} className="bg-slate-900 p-6 rounded-2xl border border-slate-800 transition-colors group hover:border-blue-500/50">
                                <div className="flex justify-between items-center mb-4">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-white font-bold text-lg border border-slate-600">{emp.name.charAt(0)}</div>
                                    <div className="text-right"><span className="text-xs text-slate-500 block">Last Review</span><span className="text-sm font-bold text-white">28 Days Ago</span></div>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-1">{emp.name}</h3>
                                <p className="text-slate-500 text-sm mb-6">{emp.role}</p>
                                
                                {isAdmin ? (
                                    <div className="flex flex-col gap-2">
                                        <button 
                                            onClick={() => handleStartReview(emp)}
                                            className="w-full flex items-center justify-center gap-2 text-xs font-bold text-blue-400 bg-blue-900/10 px-3 py-2 rounded-lg border border-blue-500/20 hover:bg-blue-600 hover:text-white transition-colors"
                                        >
                                            <span>Start {period} Appraisal</span> <ChevronRight size={14} />
                                        </button>
                                        <button 
                                            onClick={() => handleViewHistory(emp)}
                                            className="w-full flex items-center justify-center gap-2 text-xs font-bold text-slate-400 bg-slate-800 px-3 py-2 rounded-lg border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors"
                                        >
                                            <span>View Past Reviews</span> <History size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <button 
                                        onClick={() => handleViewHistory(emp)}
                                        className="flex items-center gap-2 text-xs font-bold text-slate-400 bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-700 hover:bg-slate-700 hover:text-white transition-colors w-fit"
                                    >
                                        <span>View Past Reviews</span> <History size={14} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* VIEW 2: REVIEW FORM (Manager Only) */}
            {viewState === 'form' && selectedStaff && (
                <div className="max-w-6xl mx-auto pb-12">
                    <button onClick={() => setViewState('list')} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2 text-sm font-bold">&larr; Back to Staff List</button>
                    
                    <div className="mb-8">
                        <h3 className="text-2xl font-bold text-white mb-1">{selectedStaff.name}</h3>
                        <p className="text-slate-400 text-sm">{selectedStaff.role} • {period} Performance Review</p>
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 text-center">
                            <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">{selectedStaff.metrics.sales ? 'Sales Volume' : 'Tickets Resolved'}</span>
                            <div className="text-3xl font-bold text-white mt-2">{selectedStaff.metrics.sales || selectedStaff.metrics.tickets || 0}</div>
                        </div>
                        <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 text-center">
                            <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">{selectedStaff.metrics.calls ? 'Calls / Comms' : 'KYC Checks'}</span>
                            <div className="text-3xl font-bold text-blue-400 mt-2">{selectedStaff.metrics.calls || selectedStaff.metrics.kyc || 0}</div>
                        </div>
                        <div className="bg-slate-950 p-6 rounded-xl border border-slate-800 text-center">
                            <span className="text-xs text-slate-500 uppercase font-bold tracking-wider">Avg CSAT</span>
                            <div className="text-3xl font-bold text-amber-400 mt-2">{selectedStaff.metrics.csat || 0.0}</div>
                        </div>
                    </div>

                    <div className="bg-slate-900 border border-indigo-500/30 p-6 rounded-xl mb-8 relative overflow-hidden shadow-lg shadow-indigo-900/10">
                        <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                        <h4 className="text-indigo-400 font-bold flex items-center gap-2 mb-3"><Sparkles size={18} /> Agentic AI Performance Analysis</h4>
                        <p className="text-slate-300 text-sm mb-4 leading-relaxed">{selectedStaff.name} has demonstrated consistent adherence to protocols over the last month.</p>
                        <div className="space-y-1 bg-slate-950/50 p-4 rounded-lg border border-slate-800">
                            <p className="text-xs flex gap-2"><span className="text-green-400 font-bold min-w-[80px]">Strengths:</span> <span className="text-slate-400">High CSAT Score, Low Rejection Rate.</span></p>
                            <p className="text-xs flex gap-2"><span className="text-red-400 font-bold min-w-[80px]">Growth Areas:</span> <span className="text-slate-400">Occasional delay in email follow-ups.</span></p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <div className="space-y-6">
                            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl flex flex-col gap-6 h-full">
                                <h3 className="font-bold text-white flex items-center gap-2"><ClipboardList size={20} className="text-slate-400" /> Manager Assessment</h3>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Overall Rating</label>
                                    <div className="flex gap-1">
                                        {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                            <button key={num} onClick={() => setFormData({...formData, rating: num})} className={`flex-1 h-9 rounded text-xs font-bold transition-all ${formData.rating === num ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-500 hover:bg-slate-700'}`}>{num}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Manager's Evaluation</label>
                                    <textarea className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-white h-48 focus:outline-none focus:border-blue-500 transition-colors" placeholder="Evaluation..." value={formData.managerComments} onChange={(e) => setFormData({...formData, managerComments: e.target.value})}></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-6">
                            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl h-full">
                                <h3 className="font-bold text-white mb-4 flex items-center gap-2"><UserCheck size={20} className="text-blue-500" /> Staff Feedback</h3>
                                <p className="text-xs text-slate-500 mb-4">To be completed by the staff member.</p>
                                <textarea className="w-full bg-slate-950 border border-slate-700 rounded-xl p-4 text-sm text-white h-64 focus:outline-none focus:border-blue-500 transition-colors" placeholder="Comments..." value={formData.staffComments} onChange={(e) => setFormData({...formData, staffComments: e.target.value})}></textarea>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 p-6 bg-slate-900 border border-slate-800 rounded-2xl">
                        <button onClick={() => { alert('Review Signed & Saved!'); setViewState('list'); }} className="w-full bg-green-600 hover:bg-green-500 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-colors shadow-lg shadow-green-900/20">
                            <Key size={18} /> Finalize & Submit Review
                        </button>
                    </div>
                </div>
            )}

            {/* VIEW 3: HISTORY LIST (Shared View) */}
            {viewState === 'history' && selectedStaff && (
                <div className="max-w-4xl mx-auto pb-12">
                    
                    {isAdmin && (
                        <button onClick={() => setViewState('list')} className="text-slate-400 hover:text-white mb-6 flex items-center gap-2 text-sm font-bold">&larr; Back to Staff List</button>
                    )}

                    {!isAdmin && <div className="mb-8"></div>}
                    
                    <div className="flex justify-between items-end mb-8">
                        <div>
                            <h3 className="text-2xl font-bold text-white mb-1">Performance History</h3>
                            <p className="text-slate-400 text-sm">Completed reviews for {selectedStaff.name}</p>
                        </div>
                        <div className="text-right">
                             <div className="text-3xl font-bold text-white">{(selectedStaff.reviews || []).filter(r => r.status === 'Completed').length}</div>
                             <p className="text-xs text-slate-500 uppercase font-bold">Total Reviews</p>
                        </div>
                    </div>

                    <div className="space-y-4">
                        {(selectedStaff.reviews || []).filter(r => r.status === 'Completed').length > 0 ? (
                            selectedStaff.reviews.filter(r => r.status === 'Completed').map((review) => (
                                <div 
                                    key={review.id} 
                                    onClick={() => setViewingHistoryItem(review)}
                                    className="bg-slate-900 border border-slate-800 p-6 rounded-xl flex items-center justify-between group hover:border-blue-500/50 transition-all cursor-pointer hover:bg-slate-800/50"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-lg bg-blue-900/20 text-blue-400 flex items-center justify-center border border-blue-500/20 font-bold text-lg">
                                            {review.rating}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-white group-hover:text-blue-400 transition-colors">{review.type} Review</h4>
                                            <p className="text-sm text-slate-500">Conducted on {review.date}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <p className="text-xs text-slate-500 uppercase font-bold">Manager</p>
                                            <p className="text-sm text-slate-300">{review.manager || 'Admin'}</p>
                                        </div>
                                        {/* LIST VIEW DOWNLOAD BUTTON - CONNECTED */}
                                        <button 
                                            onClick={(e) => handleDownloadReport(review, selectedStaff.name, e)}
                                            className="bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold px-4 py-2 rounded-lg transition-colors flex items-center gap-2 border border-slate-700 hover:border-slate-600"
                                        >
                                            <Download size={14} /> PDF
                                        </button>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center p-12 border-2 border-dashed border-slate-800 rounded-xl">
                                <History size={48} className="mx-auto text-slate-700 mb-4" />
                                <h3 className="text-slate-500 font-bold">No History Available</h3>
                                <p className="text-sm text-slate-600 mt-1">There are no completed reviews on file for this account.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- MAIN PAGE COMPONENT ---
// FIXED: Removed 'role' prop here to avoid naming conflict. We use 'currentUser' or 'userRole' prop.
const TeamHub = ({ currentUser, role, userRole = 'Admin' }) => {
    // 1. Resolve Role: Prefer currentUser prop, fallback to userRole
    const activeRole = currentUser?.role || role || userRole;
    
    const [activeTab, setActiveTab] = useState('overview');
    
    // 1. STATE MANAGEMENT
    const [staffData, setStaffData] = useState(INITIAL_STAFF);
    const [ruleData, setRuleData] = useState(INITIAL_RULES);

    // 2. PERMISSION & IDENTITY LOGIC
    // This determines "Who am I?" and "Who can I see?"
    const getVisibleStaff = () => {
        // A. ADMIN: Sees Everything
        if (activeRole === 'Admin') return staffData;

        // B. TEAM LEADS: See their specific department
        if (activeRole.includes('Support_Manager') || activeRole.includes('Support Lead')) {
            // Managers see Support AND Hybrid
            return staffData.filter(s => s.role.includes('Support') || s.role.includes('Hybrid'));
        }
        if (activeRole.includes('Sales_Manager') || activeRole.includes('Sales Lead')) {
            // Managers see Sales AND Hybrid
            return staffData.filter(s => s.role.includes('Sales') || s.role.includes('Hybrid'));
        }

        // C. STANDARD AGENTS: See ONLY themselves
        let myId = null;
        if (activeRole === 'Sales_L1' || activeRole.includes('Sales')) myId = 'ST-001'; 
        if (activeRole === 'Support_L1' || activeRole.includes('Support')) myId = 'ST-002'; 
        // FIXED: Hybrid role now maps correctly to ST-004
        if (activeRole === 'Hybrid_L1' || activeRole.includes('Hybrid')) myId = 'ST-004'; 
        
        return staffData.filter(s => s.id === myId);
    };

    const displayStaff = getVisibleStaff();
    const isAdmin = ['Admin', 'Support_Manager', 'Sales_Manager'].includes(activeRole);

    return (
        <div className="p-6 md:p-12 pb-24 max-w-7xl mx-auto min-h-screen">
            <div className="flex justify-between items-end mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Team Hub</h1>
                    <p className="text-slate-400">Workforce management & operations</p>
                </div>
                <div className="flex justify-between items-end mb-8">
    <div>
        <h1 className="text-3xl font-bold text-white mb-2">Team Hub</h1>
        <p className="text-slate-400">Workforce management & operations</p>
    </div>
</div>
            </div>

            {/* TAB NAVIGATION - Renamed 'bonus structure' to 'Pay' & 'kpi' to 'KPI' */}
            <div className="border-b border-slate-800 mb-8 flex space-x-8 overflow-x-auto">
                {['overview', 'KPI', 'Pay', 'rotas', 'hr & reviews', 'appraisals'].map(tab => (
                    <button 
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-4 text-sm font-bold capitalize transition-colors border-b-2 ${activeTab === tab ? 'border-blue-500 text-blue-500' : 'border-transparent text-slate-500 hover:text-white'}`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

{/* CONTENT AREA */}
            <div className="min-h-[400px]">
                {activeTab === 'overview' && <OverviewTab staff={displayStaff} role={activeRole} />}
                
                {/* RENAMED to 'KPI' */}
                {activeTab === 'KPI' && <KPITab staff={displayStaff} />} 
                
                {/* RENAMED to 'Pay' & ADDED 'staff={staffData}' PROP (Fixes the crash) */}
                {activeTab === 'Pay' && <BonusTab role={activeRole} rules={ruleData} setRules={setRuleData} staff={staffData} />}
                
                {activeTab === 'rotas' && <RotaTab staff={displayStaff} setStaff={setStaffData} role={activeRole} />}
                
                {/* --- FIXED SECTION START --- */}
                {/* We wrap both HRTab and GrievanceSection in a div so they appear together ONLY on this tab */}
                {activeTab === 'hr & reviews' && (
                    <div className="space-y-8">
                        {/* GrievanceSection is now INSIDE HRTab, so we don't need it here */}
                        <HRTab staff={displayStaff} setStaff={setStaffData} role={activeRole} currentUser={currentUser} />
                    </div>
                )}
                {/* --- FIXED SECTION END --- */}
                
                {activeTab === 'appraisals' && <AppraisalsTab staff={displayStaff} role={activeRole} />}
            </div>
        </div>
    );
};

export default TeamHub;